/** ngx-mask: A = alfanumérico (letras+dígitos), os últimos 2 dígitos são sempre numéricos (DV). */
export const CNPJ_MASK = 'AA.AAA.AAA/AAAA-00';

const CNPJ_BODY_LENGTH = 12;
const WEIGHTS1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const WEIGHTS2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_FORMAT_REGEX = /^[A-Z0-9]{12}\d{2}$/;

/**
 * Remove pontuação e mantém A–Z / 0–9 em maiúsculas (máx. 14 caracteres).
 * Body (primeiros 12): alfanumérico. DV (últimos 2): dígitos apenas.
 */
export function unformatCnpj(value: string | null | undefined): string {
  if (!value) return '';
  const alnum = value
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  const body = alnum.slice(0, CNPJ_BODY_LENGTH);
  const dv = alnum.slice(CNPJ_BODY_LENGTH).replace(/[^0-9]/g, '').slice(0, 2);
  return `${body}${dv}`;
}

/**
 * Formata como XX.XXX.XXX/XXXX-XX (progressivo para entrada parcial).
 */
export function formatCnpj(value: string | null | undefined): string {
  const cleaned = unformatCnpj(value);
  if (!cleaned) return '';

  if (cleaned.length <= 2) {
    return cleaned;
  }
  if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
  }
  if (cleaned.length <= 8) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
  }
  if (cleaned.length <= 12) {
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
  }
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
}

/** True quando o corpo (não mascarado) CNPJ contém pelo menos uma letra. */
export function isAlphanumericCnpj(value: string | null | undefined): boolean {
  const cleaned = unformatCnpj(value);
  return /[A-Z]/.test(cleaned.slice(0, CNPJ_BODY_LENGTH));
}

/**
 * Valida CNPJ (numérico legado ou alfanumérico) conforme IN RFB 2.229/2024.
 * Valor do caractere = ASCII code − 48 (dígitos inalterados; A=17 … Z=42).
 */
export function isValidCnpj(value: string | null | undefined): boolean {
  const cnpj = unformatCnpj(value);
  if (!CNPJ_FORMAT_REGEX.test(cnpj)) {
    return false;
  }

  // Rejeita todos os caracteres idênticos (ex: 00000000000000)
  if (/^(.)\1{13}$/.test(cnpj)) {
    return false;
  }

  const digit1 = calcCheckDigit(cnpj.slice(0, 12), WEIGHTS1);
  if (Number(cnpj[12]) !== digit1) {
    return false;
  }

  const digit2 = calcCheckDigit(cnpj.slice(0, 13), WEIGHTS2);
  return Number(cnpj[13]) === digit2;
}

function calcCheckDigit(base: string, weights: number[]): number {
  let sum = 0;
  for (let i = 0; i < base.length; i++) {
    sum += (base.charCodeAt(i) - 48) * weights[i];
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}
